"use client"

import { useEffect, useState } from "react"
import { Loader2 } from "lucide-react"
import { getShippingPreview, type ShippingPreviewOption } from "@lib/data/fulfillment"
import { SHIPPING_PREVIEW_COUNTRIES } from "@constants/product-page"
import { convertToLocale } from "@lib/util/money"

const DEFAULT_COUNTRY_CODE = "gb"

const countryName = (countryCode: string | null) =>
  SHIPPING_PREVIEW_COUNTRIES.find((country) => country.code === countryCode)?.name ??
  countryCode?.toUpperCase() ??
  null

export default function ShippingMethodWidget() {
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE)
  const [shipsFrom, setShipsFrom] = useState<string | null>(null)
  const [options, setOptions] = useState<ShippingPreviewOption[]>([])
  const [status, setStatus] = useState<"loading" | "done" | "error">("loading")

  useEffect(() => {
    let cancelled = false

    setStatus("loading")
    getShippingPreview(countryCode).then((preview) => {
      if (cancelled) return
      if (!preview) {
        setStatus("error")
        return
      }
      setShipsFrom(countryName(preview.ships_from_country_code))
      setOptions(preview.shipping_options)
      setStatus("done")
    })

    return () => {
      cancelled = true
    }
  }, [countryCode])

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pb-4 border-b border-gray-200">
        <div className="flex items-center gap-2.5">
          <label htmlFor="shipping-preview-country" className="text-base font-bold text-gray-900">
            Ships to
          </label>
          <select
            id="shipping-preview-country"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            className="border border-brand-navy rounded-md px-2.5 py-2 text-sm text-gray-900 bg-gray-100"
          >
            {SHIPPING_PREVIEW_COUNTRIES.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2.5">
          <span className="text-base font-bold text-gray-900">Ships from</span>
          <span className="text-sm text-gray-700">{shipsFrom ?? "—"}</span>
        </div>
      </div>
      <div className="pt-4">
        <p className="text-base font-bold text-gray-900 mb-2">Shipping Method</p>
        {status === "loading" && (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-1">
            <Loader2 className="w-4 h-4 animate-spin" />
            Checking rates...
          </div>
        )}
        {status === "error" && (
          <p className="text-sm text-gray-500">
            Shipping rates aren&apos;t available for this destination right now.
          </p>
        )}
        {status === "done" && options.length === 0 && (
          <p className="text-sm text-gray-500">
            No shipping methods are currently available for this destination.
          </p>
        )}
        {status === "done" && options.length > 0 && (
          <ul className="space-y-1.5">
            {options.map((option) => (
              <li key={option.id} className="flex items-baseline justify-between gap-4 text-sm">
                <span className="text-gray-700">
                  {option.name}
                  {option.description ? ` - ${option.description}` : ""}
                </span>
                <span className="font-semibold text-gray-900 whitespace-nowrap">
                  {convertToLocale({ amount: option.amount, currency_code: option.currency_code })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
